import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { Spin } from "antd";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";
import LostMarksBar from "./LostMarksBar";
import ImprovementSummaryCards from "./ImprovementSummaryCards";
import { SEVERITY_GRADIENT } from "./improvementSeverity";
import StoreWiseLostMarksBarChart from "../../../components/modals/StoreWiseLostMarksBarChart";
import { CYCLE_COLORS } from "../../../utils/Service/colors";
import CycleBar from "../../../components/common/CycleBar";
import CardHeader from "../../../components/common/CardHeader";

const PAGE_SIZE = 10;

const ImprovementAreas = ({
  cycleIds,
  cycles,
  impLoading,
  data,
  downloading,
  downloadImprovableTable,
}) => {
  const [showModalOpen, setShowModalOpen] = useState(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [page, setPage] = useState(1);

  const summaryData = data?.summary || {};
  const cycleSummaryData = data?.cycle_summary || [];
  const questionComparisonData = data?.question_comparison || [];

  const cycleById = useMemo(() => {
    const map = {};

    cycles.forEach((cycle) => {
      map[cycle.id] = cycle;
    });
    return map;
  }, [cycles]);

  const selectedCards = useMemo(() => {
    return cycleIds
      .map((id, index) => {
        const cycle = cycleById[id];
        if (!cycle) return null;

        return {
          ...cycle,
          id,
          color: CYCLE_COLORS[index % CYCLE_COLORS.length].main,
        };
      })
      .filter(Boolean);
  }, [cycleIds, cycleById]);

  const allQuestions = useMemo(() => {
    return questionComparisonData.map((question, index) => {
      const cycleData = {};

      question.cycles?.forEach((cycle) => {
        cycleData[cycle.audit_cycle_id] = {
          questionId: cycle.question_id,
          maxMarks: cycle.max_marks,
          obtainedMarks: cycle.obtained_marks,
          lostMarks: cycle.lost_marks,
          percentage: cycle.percentage,
        };
      });

      const firstCycle = question.cycles?.[0];

      return {
        id: `${question.section_id}-${index}`,
        sectionId: question.section_id,
        section: question.section_name,
        text: question.question_txt,
        totalMarks: firstCycle?.max_marks || 0,
        cycleData,
      };
    });
  }, [questionComparisonData]);

  const totalPages = Math.max(1, Math.ceil(allQuestions.length / PAGE_SIZE));

  const pageQuestions = allQuestions.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [allQuestions.length]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (page <= 3) {
      return [1, 2, 3, "...", totalPages];
    }

    if (page >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", page, "...", totalPages];
  }, [page, totalPages]);

  const summaryCards = useMemo(() => {
    return selectedCards.map((cycle) => {
      const cycleSummary = cycleSummaryData.find(
        (item) => item.audit_cycle_id === cycle.id,
      );

      const possibleTotal = cycleSummary?.total_max_marks || 0;

      const obtainedTotal = cycleSummary?.total_obtained_marks || 0;

      const lostTotal = possibleTotal - obtainedTotal;

      return {
        ...cycle,
        lostTotal,
        possibleTotal,
        obtainedTotal,
      };
    });
  }, [selectedCards, cycleSummaryData]);

  const showModal = (questionId) => {
    setSelectedQuestionId(questionId);
    setShowModalOpen(true);
  };

  const closeModal = () => {
    setShowModalOpen(false);
    setSelectedQuestionId(null);
  };

  return (
    <Wrapper className="dashboard-card shadow p-3">
      <CardHeader
        title="Improvement Areas – Lost Marks Comparison&nbsp;"
        tooltipTitle="Lost marks = points missed out of the total available for that question."
        subtitle="Compare lost marks up to 5 audit cycles to identify key areas of improvement."
        downloading={downloading}
        onDownloadClick={() => downloadImprovableTable("improvement-areas")}
      />
      <SlotRow>
        <CycleBar cycleIds={cycleIds} cycles={cycles} />

        <Legend>
          <LegendText>Higher lost marks</LegendText>
          <GradientSwatch />
          <LegendText>Lower lost marks</LegendText>
        </Legend>
      </SlotRow>

      <div>
        <ImprovementSummaryCards
          totalQuestions={
            summaryData.total_questions || questionComparisonData.length || 0
          }
          cards={summaryCards}
        />
        <TableWrap className="upAuditsCard mt-3">
          <Grid columns={selectedCards.length}>
              <HeaderCell first>Sr. No.</HeaderCell>
              <HeaderCell first>Section</HeaderCell>
              <HeaderCell first wide>
                Questions
              </HeaderCell>
              {selectedCards.map((cycle) => (
                <HeaderCellGroup key={cycle.id} color={cycle.color}>
                  <CycleHeading color={cycle.color}>{cycle.name}</CycleHeading>
                  <CycleDate>{cycle.date}</CycleDate>
                  <MetricLabel>Lost Marks (Obtained / Total)</MetricLabel>
                </HeaderCellGroup>
              ))}
            {impLoading ? (
              <Loader>
                <Spin />
              </Loader>
            ) : (
              pageQuestions.map((q, rowIndex) => (
                  <React.Fragment key={q.id}>
                    <Cell first>{(page - 1) * PAGE_SIZE + rowIndex + 1}</Cell>
                    <Cell first>{q.section}</Cell>
                    <Cell first wide>
                      {q.text}
                    </Cell>
                    {selectedCards.map((cycle) => {
                      const cycleQuestion = q.cycleData?.[cycle.id];
                      const obtained = cycleQuestion?.obtainedMarks || 0;
                      const total = cycleQuestion?.maxMarks || 0;
                      const lost = cycleQuestion?.lostMarks || 0;
                      return (
                        <Cell
                          key={cycle.id}
                          clickable={Boolean(
                            cycleQuestion?.questionId && lost !== 0,
                          )}
                          onClick={() => {
                            if (cycleQuestion?.questionId && lost !== 0) {
                              showModal(cycleQuestion.questionId);
                            }
                          }}
                        >
                          <CellInner>
                            <ObtainedText>
                              {obtained}/{total}
                            </ObtainedText>

                            <BarWrapper>
                              <LostMarksBar obtained={obtained} total={total} />
                            </BarWrapper>

                            <RightOutlined
                              style={{
                                fontSize: "0.7rem",
                                color: "#B5BAC2",
                                flexShrink: 0,
                              }}
                            />
                          </CellInner>
                        </Cell>
                      );
                    })}
                  </React.Fragment>
              ))
            )}
          </Grid>
        </TableWrap>
        {!impLoading && allQuestions.length > 0 && (
            <PaginationRow>
              <PageInfo>
                Showing {(page - 1) * PAGE_SIZE + 1} to{" "}
                {Math.min(page * PAGE_SIZE, allQuestions.length)} of{" "}
                {allQuestions.length} questions
              </PageInfo>
              <Pager>
                <PageArrow
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <LeftOutlined />
                </PageArrow>
                {pageNumbers.map((n, i) =>
                  n === "..." ? (
                    <Ellipsis key={`e-${i}`}>...</Ellipsis>
                  ) : (
                    <PageNum
                      key={n}
                      active={n === page}
                      onClick={() => setPage(n)}
                    >
                      {n}
                    </PageNum>
                  ),
                )}
                <PageArrow
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  <RightOutlined />
                </PageArrow>
              </Pager>
            </PaginationRow>
        )}
      </div>

      {/* MODAL */}
      <StoreWiseLostMarksBarChart
        isModalOpen={showModalOpen}
        questionId={selectedQuestionId}
        closeModal={closeModal}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  font-family: "DM Sans", sans-serif;
`;

const SlotRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendText = styled.span`
  font-size: 0.78rem;
  color: #353e4c;
  white-space: nowrap;
`;

const GradientSwatch = styled.div`
  width: 120px;
  height: 10px;
  border-radius: 999px;
  background: ${SEVERITY_GRADIENT};
`;

const TableWrap = styled.div`
  border: 1px solid #eaecef;
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
  padding-bottom: 0.05rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 64px 200px minmax(260px, 1fr) repeat(
      ${(props) => props.columns},
      minmax(170px, 1fr)
    );
  min-width: ${(props) => 64 + 200 + 260 + props.columns * 170}px;
`;

const HeaderCell = styled.div`
  background: #f8f9fb;
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #eaecef;
  border-right: 1px solid #eaecef;
  font-weight: 700;
  color: #16325c;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
`;

const HeaderCellGroup = styled.div`
  background: #f8f9fb;
  padding: 0.7rem 1rem;
  border-bottom: 2px solid ${(props) => props.color};
  border-right: 1px solid #eaecef;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CycleHeading = styled.span`
  font-weight: 700;
  font-size: 0.85rem;
  color: ${(props) => props.color};
`;

const CycleDate = styled.span`
  font-size: 0.72rem;
  color: #8a94a6;
`;

const MetricLabel = styled.span`
  font-size: 0.68rem;
  color: #8a94a6;
  margin-top: 4px;
`;

const Cell = styled.div`
  padding: 0.35rem 1rem;
  border-bottom: 1px solid #eaecef;
  border-right: 1px solid #eaecef;
  font-size: 0.83rem;
  color: #353e4c;
  display: flex;
  align-items: center;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  &:hover {
    background: ${(props) => (props.clickable ? "#F8F9FB" : "transparent")};
  }
`;
const CellInner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;

const ObtainedText = styled.span`
  font-weight: 700;
  color: #16325c;
  white-space: nowrap;
  flex-shrink: 0;
  max-width: 5rem;
  min-width:3rem;
`;

const BarWrapper = styled.div`
  flex: 1;
  min-width: 20px;
`;

const PaginationRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
`;

const PageInfo = styled.span`
  font-size: 0.8rem;
  color: #8a94a6;
`;

const Pager = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PageArrow = styled.button`
  border: 1px solid #dde1e6;
  background: #ffffff;
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  color: ${(props) => (props.disabled ? "#DDE1E6" : "#353E4C")};
`;

const PageNum = styled.button`
  border: 1px solid ${(props) => (props.active ? "#007DC1" : "#DDE1E6")};
  background: ${(props) => (props.active ? "#007DC1" : "#ffffff")};
  color: ${(props) => (props.active ? "#ffffff" : "#353E4C")};
  border-radius: 6px;
  width: 30px;
  height: 30px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const Ellipsis = styled.span`
  color: #8a94a6;
  padding: 0 4px;
`;

const Loader = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;
export default ImprovementAreas;
